import { Box, Stack, Typography, IconButton, List, ListItem, ListItemText, Tooltip } from "@mui/material";
import { useState, useEffect, useRef, useMemo } from "react";
import CloseIcon from '@mui/icons-material/Close';
import elevator from '../../../assets/elevator.png';
import { generalStatus } from "../../../utils/DummyData";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { COLOR } from "../../../utils/colors";
import { getLiftStatusColor } from "../../../utils/ColourStatus";
import { useLiftStatus, useLiftStatusWebsocket } from "../../../hooks/lift";
import { LiftJobType, LiftStatusResponse } from "../../../hooks/lift/index.type";
import { getStatusLabel } from "../../../hooks/lift/_function";
import { VIEW } from "../../../utils/breakpoint";
import { Wifi, WifiOff } from "@mui/icons-material";
import { shadow } from "../../../utils/styles";

interface GeneralDetails {
  door: StatusDetails[];
  lift: LiftStatusDetails[];
}

interface StatusDetails {
  name: string;
  status: string;
  connected: boolean;
}

interface LiftStatusDetails {
  name: string;
  tag?: string;
  current_floor?: string;
  status: string;
  connected: boolean;
  robots?: RobotLiftType[];
}

type RobotLiftType = {
  name: string,
  status: string,
  from_floor: string,
  destination_floor: string
}

interface GeneralStatusProps {
  handleStatusBoardClose?: () => void;
  isOpen: boolean;
}

const GeneralStatus: React.FC<GeneralStatusProps> = ({ handleStatusBoardClose, isOpen }) => {
  const [isShowContent, setIsShowContent] = useState(false);
  const [selectedLiftStatusFilter, setSelectedLiftFilter] = useState('');
  const [liftOfBuilding, setLiftOfBuiling] = useState<string[]>([]);
  const [liveLiftStatuses, setLiveLiftStatuses] = useState<LiftStatusResponse>([]);

  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  useEffect(() => {
    setIsShowContent(true);
  }, []);

  const {
    data: dataLift,
    isLoading: isLoadingLiftStatus
  } = useLiftStatus({
    enabled: isOpen,
    building: selectedLiftStatusFilter?.toLowerCase() === 'all'
      ? undefined
      : selectedLiftStatusFilter || undefined
  });

  const normalizeLiftData = (payload: any): LiftStatusResponse => {
    if (!payload) return [];

    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.data)) {
      return payload.data;
    }

    if (Array.isArray(payload?.results)) {
      return payload.results;
    }

    if (payload?.lift_name) {
      return [payload];
    }

    return [];
  };

  const mergeLiftStatuses = (
    prev: LiftStatusResponse,
    incoming: LiftStatusResponse
  ): LiftStatusResponse => {
    if (!incoming || incoming.length === 0) {
      return prev;
    }

    const map = new Map<string, any>();

    prev.forEach((item: any) => {
      const key = item?.lift_name || `${item?.building}-${item?.current_level}`;
      map.set(key, item);
    });

    incoming.forEach((item: any) => {
      const key = item?.lift_name || `${item?.building}-${item?.current_level}`;
      map.set(key, item);
    });

    return Array.from(map.values());
  };

  useEffect(() => {
    if (!dataLift) return;

    const normalized = normalizeLiftData(dataLift);

    if (normalized.length === 0) return;

    setLiveLiftStatuses((prev) => {
      if (prev.length === 0) {
        return normalized;
      }
      return prev;
    });
  }, [dataLift]);

  useLiftStatusWebsocket({
    autoReconnect: true,
    onMessage: (message) => {
      if (!isOpenRef.current) {
        return;
      }

      const normalized = normalizeLiftData(message);

      if (normalized.length === 0) {
        return;
      }

      setLiveLiftStatuses((prev) => mergeLiftStatuses(prev, normalized));
    }
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const sourceData = liveLiftStatuses.length > 0 ? liveLiftStatuses : normalizeLiftData(dataLift);

    if (sourceData.length === 0) {
      return;
    }

    const dataTabValues = sourceData
      .map((item: any) => item.building)
      .filter(Boolean);

    const uniqueTabValues = dataTabValues.filter((value, index, self) => self.indexOf(value) === index);
    const finalTabValues = ['All', ...uniqueTabValues];

    setLiftOfBuiling(finalTabValues);

    if (!selectedLiftStatusFilter) {
      setSelectedLiftFilter(finalTabValues[0]);
    }
  }, [isOpen, liveLiftStatuses, dataLift, selectedLiftStatusFilter]);

  const handleLiftFilter = (newValue: string) => {
    if (newValue === selectedLiftStatusFilter) {
      return;
    }
    setSelectedLiftFilter(newValue);
  };

  const liftStatuses = useMemo(() => {
    const sourceData = liveLiftStatuses.length > 0
      ? liveLiftStatuses
      : normalizeLiftData(dataLift);

    if (!selectedLiftStatusFilter || selectedLiftStatusFilter.toLowerCase() === 'all') {
      return sourceData;
    }

    return sourceData.filter(
      (lift: any) => lift.building === selectedLiftStatusFilter
    );
  }, [liveLiftStatuses, dataLift, selectedLiftStatusFilter]);

  const generalInfo: GeneralDetails = generalStatus;

  const filterRobotJobs = (arr: LiftJobType[], map = new Map()) => {
    arr.forEach(o => map.set(o.robot_name, o));
    return [...map.values() as any] as LiftJobType[];
  };

  const getLevelLabel = (numString?: string) => {
    if (!numString) {
      return '';
    }

    const num = parseInt(numString);
    if (num < 0) {
      return 'B' + Math.abs(num);
    }
    if (num === 0) {
      return 'G';
    }
    return 'L' + num;
  };

  const getRobotLabel = (jobs?: LiftJobType[]) => {
    if (!jobs || jobs.length === 0) {
      return '';
    }

    return filterRobotJobs(jobs).length;
  };

  return (
    <>
      {isOpen && (
        <Stack
          sx={(theme) => ({
            [theme.breakpoints.up(VIEW.PHONE)]: {
              position: 'fixed',
              inset: 0,
              height: '100vh',
              width: '100vw',
              zIndex: 1100,
              marginTop: '0px !important'
            },
            [theme.breakpoints.up(VIEW.TABLET)]: {
              opacity: isShowContent ? 1 : 0,
              right: "20px",
              width: "400px",
              bgcolor: "white",
              boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
              borderRadius: "10px",
              height: "54vh",
              position: "absolute",
              top: "60px",
              left: 'auto',
              bottom: 'auto',
              maxHeight: "450px",
              zIndex: 1005
            },
            [theme.breakpoints.up(VIEW.DESKTOP)]: {
              top: "60px",
              height: "450px",
            },
            opacity: isShowContent ? 1 : 0,
            bgcolor: "white",
            boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
            borderRadius: "10px",
            transition: 'all',
            transitionDuration: '300ms',
            overflow: 'hidden'
          })}
        >
          <>
            <Box sx={{ height: "88px", display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", flexDirection: "column", width: "97%", m: 1 }}>
                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", m: 1 }}>
                  <Typography sx={{ color: "#000", ml: 1, fontWeight: "600", fontSize: "16px", mt: 0.2 }}>
                    Lift Status
                  </Typography>
                  <IconButton onClick={handleStatusBoardClose}>
                    <CloseIcon sx={{ color: "#000", fontSize: "20px" }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mt: -3.5, display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
              <Tabs
                value={selectedLiftStatusFilter}
                onChange={(_, newValue) => handleLiftFilter(newValue)}
                sx={{
                  ml: 2,
                  mt: 0.5,
                  fontSize: "8px",
                  minHeight: '32px',
                  '.MuiTabs-indicator': {
                    display: 'none',
                    height: '0px !important',
                  },
                  '& .MuiTab-root': {
                    color: '#A0A0A0',
                    fontSize: '12px',
                    height: '20px !important',
                    minHeight: '20px',
                    '&.Mui-selected': {
                      color: '#087D46',
                      backgroundColor: '#00E58E',
                      borderRadius: '30px',
                    },
                  },
                }}
              >
                {liftOfBuilding.map((level, index) => (
                  <Tab key={index} value={level} label={level} />
                ))}
              </Tabs>

              <List sx={{ height: '100%', maxHeight: '47.55vh', overflowY: "auto", overflowX: "hidden", mt: 0, pt: 0.5 }}>
                {liftStatuses.map((lift: any, index: number) => (
                  <ListItem key={index} sx={{ py: 1.5, paddingInline: "24px" }}>
                    <Box
                      sx={{
                        flex: 'none',
                        position: 'relative',
                        width: "60px",
                        height: "60px",
                        bgcolor: "#f5f5f5",
                        color: "white",
                        fontWeight: "600",
                        borderRadius: "30px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        mr: '36px'
                      }}
                    >
                      <img src={elevator} style={{ width: "40px", height: "40px" }} />
                      <Box
                        sx={{
                          position: "absolute",
                          top: '-14px',
                          right: '-14px',
                          aspectRatio: "1/1",
                          color: '#378FFE',
                          backgroundColor: '#fff',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: '6px',
                          border: `2px solid #BBDAFF`,
                          ...shadow
                        }}
                      >
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#378FFE' }}>
                          {getLevelLabel(lift.current_level)}
                        </Typography>
                      </Box>
                    </Box>

                    <ListItemText>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "right", paddingY: "4px" }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography sx={{ color: "#000", fontSize: "16px", fontWeight: "700" }}>
                              {lift.lift_name}
                            </Typography>
                            {lift.connected ? (
                              <Tooltip title="Online" placement="top" arrow>
                                <Wifi sx={{ color: COLOR.accent.green, fontSize: '14px', mb: '1px' }} />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Offline" placement="top" arrow>
                                <WifiOff sx={{ color: COLOR.accent.danger, fontSize: '14px', mb: '1px' }} />
                              </Tooltip>
                            )}
                          </Box>
                        </Box>

                        <Typography sx={{ color: "#727272", fontSize: "12px" }}>
                          Status:
                          <span style={{ color: getLiftStatusColor(lift.status), marginLeft: "5px", textTransform: 'capitalize' }}>
                            {lift.status?.toLowerCase() === 'free'
                              ? 'Stationary'
                              : lift.status?.toLowerCase()?.trim()?.replace(/_/g, ' ')}
                          </span>
                        </Typography>

                        <Typography sx={{ color: "#727272", fontSize: "12px" }}>
                          {lift.lift_job && lift.lift_job.length > 0
                            ? `AMR: ${lift.lift_job[0].robot_name}`
                            : 'No AMR in the lift'}
                        </Typography>

                        {lift.lift_job && lift.lift_job.length > 0 && (
                          <Box sx={{ userSelect: 'none' }}>
                            <Box sx={{ mt: '6px' }}>
                              {filterRobotJobs(lift.lift_job || []).map((job, jobIndex) => (
                                <Typography key={jobIndex} sx={{ color: "#727272", fontSize: "12px" }}>
                                  {getStatusLabel(job.lift_job_status, job)}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </ListItemText>
                  </ListItem>
                ))}
              </List>

              {!isLoadingLiftStatus && liftStatuses.length === 0 && (
                <Box sx={{ mt: 0, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography style={{ color: COLOR.gray[400] }}>No data available</Typography>
                </Box>
              )}
            </Box>
          </>
        </Stack>
      )}
    </>
  );
};

export default GeneralStatus;